import type { PlannedTodayRow } from './today-horizons';
import type { TodayEntry } from './today-launches';

/**
 * « Une entreprise, une ligne » (21/10/2026): the tasks of one company are one
 * entry — its first task in front, the others one level down. Only an exact
 * identity groups: the same account (or one attached to it with « À
 * rattacher »), never a name. App work joins only when its task carries the
 * account; a launch stays a task of its own.
 */
export type FileEntry =
  | TodayEntry
  | { kind: 'company'; key: string; items: PlannedTodayRow[] };

const companyKey = (item: PlannedTodayRow): string | null => {
  const { row } = item;
  const id = row.kind === 'account'
    ? row.item.subject.accountSubjectId ?? row.item.subject.subjectId
    : row.topic.accountSubjectId;
  return id ? `company:${id}` : null;
};

/** A group takes the place of its first task; a company with one task stays a row. */
export const groupByCompany = (entries: TodayEntry[]): FileEntry[] => {
  const groups = new Map<string, PlannedTodayRow[]>();
  entries.forEach((entry) => {
    const key = entry.kind === 'row' ? companyKey(entry.item) : null;
    if (key && entry.kind === 'row') groups.set(key, [...(groups.get(key) ?? []), entry.item]);
  });
  const placed = new Set<string>();
  return entries.flatMap((entry): FileEntry[] => {
    const key = entry.kind === 'row' ? companyKey(entry.item) : null;
    const items = key ? groups.get(key) : undefined;
    if (!key || !items || items.length < 2) return [entry];
    if (placed.has(key)) return [];
    placed.add(key);
    return [{ kind: 'company', key, items }];
  });
};
